import { useTranslation } from "react-i18next";
import type { SocialCommentDetailDto } from "@multi-chat/shared";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import { formatConversationFullDate, formatConversationListTime } from "../conversation-time.js";
import { useNowMs } from "../use-now-ms.js";
import {
  isBusinessSelfAuthor,
  resolveCommentAuthorLabel,
} from "./comment-identity.js";

const LIST_TIME_REFRESH_MS = 60_000;

export function CommentThreadTimeline({
  parentComment,
  selectedComment,
  threadReplies,
  totalPostCommentsCount,
  scope,
  onToggleScope,
  onSelectComment,
}: {
  parentComment: SocialCommentDetailDto | null;
  selectedComment: SocialCommentDetailDto;
  threadReplies: SocialCommentDetailDto[];
  totalPostCommentsCount: number;
  scope: "thread" | "all_post_comments";
  onToggleScope: () => void;
  onSelectComment?: (commentId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const nowMs = useNowMs(LIST_TIME_REFRESH_MS);
  const showAll = scope === "all_post_comments";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-inbox-line-soft px-3.5 py-2">
        <p className="m-0 text-[0.78125rem] font-semibold text-inbox-muted">
          {showAll
            ? t("inbox.comments.thread.allCommentsTitle")
            : t("inbox.comments.thread.repliesTitle")}
        </p>
        {totalPostCommentsCount > 1 ? (
          <button
            type="button"
            className="rounded-md px-2 py-1 text-[0.75rem] font-semibold text-primary hover:bg-brand-mint focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
            onClick={onToggleScope}
          >
            {showAll
              ? t("inbox.comments.thread.showThreadOnly")
              : t("inbox.comments.thread.viewAll", { count: totalPostCommentsCount })}
          </button>
        ) : null}
      </div>

      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto overscroll-contain px-3.5 py-3",
          INBOX_SCROLLER_CLASS,
        )}
      >
        {parentComment ? (
          <CommentNode
            comment={parentComment}
            nowMs={nowMs}
            depth={0}
            role="parent"
            onSelect={onSelectComment}
          />
        ) : null}
        <CommentNode
          comment={selectedComment}
          nowMs={nowMs}
          depth={parentComment ? 1 : 0}
          role="selected"
        />
        {threadReplies.map((reply) => (
          <CommentNode
            key={reply.id}
            comment={reply}
            nowMs={nowMs}
            depth={parentComment ? 2 : 1}
            role={isBusinessSelfAuthor(reply.authorType) ? "business" : "reply"}
            onSelect={onSelectComment}
          />
        ))}
      </div>
    </div>
  );
}

function CommentNode({
  comment,
  nowMs,
  depth,
  role,
  onSelect,
}: {
  comment: SocialCommentDetailDto;
  nowMs: number;
  depth: number;
  role: "parent" | "selected" | "reply" | "business";
  onSelect?: (commentId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const identity = resolveCommentAuthorLabel({
    authorDisplayName: comment.authorDisplayName,
    authorUsername: comment.authorUsername,
    unknownLabel: t("inbox.comments.author.unknown"),
  });
  const businessSelf = isBusinessSelfAuthor(comment.authorType);
  const time = formatConversationListTime(comment.platformCreatedAt, i18n.language, nowMs, t);
  const fullTime = formatConversationFullDate(comment.platformCreatedAt, i18n.language);
  const interactive = onSelect != null && role !== "selected";
  const handled = comment.handlingStatus === "HANDLED";

  const indentPx = Math.min(depth, 3) * 16;

  const body = (
    <>
      <span className="mb-1 flex min-w-0 items-center gap-1.5">
        <ChannelIdentityBadge platformType={comment.platformType} size="inline" />
        <span className="min-w-0 flex-1 truncate text-[0.75rem] font-semibold text-inbox-strong" dir="auto">
          {identity.isHandleOnly ? (
            <LtrValue className="truncate">{identity.primary}</LtrValue>
          ) : (
            identity.primary
          )}
          {businessSelf ? (
            <span className="ms-1 font-medium text-inbox-muted">
              {t("inbox.comments.author.meSuffix")}
            </span>
          ) : null}
        </span>
        {handled && role === "selected" ? (
          <span className="shrink-0 rounded-full bg-inbox-line-soft px-1.5 py-px text-[10px] font-semibold text-inbox-muted">
            {t("inbox.comments.status.handled")}
          </span>
        ) : null}
        {time ? (
          <time
            className="shrink-0 text-[0.6875rem] tabular-nums text-inbox-subtle"
            dateTime={comment.platformCreatedAt}
            title={fullTime || undefined}
          >
            <LtrValue>{time}</LtrValue>
          </time>
        ) : null}
      </span>
      {identity.secondaryUsername ? (
        <span className="mb-1 block text-[0.6875rem] text-inbox-subtle">
          <LtrValue>@{identity.secondaryUsername}</LtrValue>
        </span>
      ) : null}
      <span className="block whitespace-pre-wrap text-[0.84375rem] leading-relaxed text-inbox-strong" dir="auto">
        {comment.content || t("inbox.comments.emptyBody")}
      </span>
      {comment.lifecycleState !== "ACTIVE" ? (
        <span className="mt-1.5 inline-flex rounded-full bg-inbox-line-soft px-1.5 py-px text-[10px] font-semibold text-inbox-muted">
          {t(`inbox.comments.lifecycle.${comment.lifecycleState}`)}
        </span>
      ) : null}
    </>
  );

  const className = cn(
    "relative w-full max-w-[min(100%,36rem)] rounded-xl border px-3 py-2.5 text-start",
    role === "selected" &&
      "border-primary/40 bg-surface shadow-[0_0_0_1px_rgb(23_126_103/0.12)] ring-1 ring-primary/15",
    role === "parent" && "border-line/70 bg-surface-muted/50",
    role === "reply" && "border-line/60 bg-surface",
    role === "business" && "border-primary/15 bg-brand-mint/45",
    !businessSelf && role !== "selected" && role !== "business" && "bg-surface",
  );

  const wrapperClass = cn("relative pb-2", depth > 0 && "pt-1");

  const connector =
    depth > 0 ? (
      <span
        className="pointer-events-none absolute top-0 bottom-2 w-px bg-line/80"
        style={{ insetInlineStart: indentPx - 10 }}
        aria-hidden="true"
      />
    ) : null;

  const card = interactive ? (
    <button
      type="button"
      className={cn(
        className,
        "cursor-pointer transition-colors hover:border-primary/25 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
      )}
      onClick={() => onSelect(comment.id)}
    >
      {body}
    </button>
  ) : (
    <article className={className}>{body}</article>
  );

  return (
    <div className={wrapperClass} style={{ paddingInlineStart: indentPx }}>
      {connector}
      {card}
    </div>
  );
}
